import React from "react";
import { Observer, useLocalStore, useObserver } from "mobx-react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Typography,
  Radio,
  RadioGroup
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import locale from "@bonliva-crm/locale";
import { PrimaryButton, SpinnerOverlay, TabBadge } from "@components";
import { useNavigation } from "@navigation";
import { ConsultantContractType } from "@graphql";
import { Form, FormxDebugger } from "@common/formx";
import { ContractFormStore } from "./ContractFormStore";
import { CompanyName, initialValues, RadioTrueFalse } from "./utils";
import { applyErpPrefill, ErpContractPrefill } from "./erpPrefill";
import { DynamicFieldsGroup } from "./DynamicFieldsGroup";
import { defaultFields } from "@consultants/ConsultantContract/formSchemas";
import { TabFormLayout } from "@common/forms/TabFormLayout";
import { ContractUploaderDialog } from "./ContractUploaderDialog";
import { useTenant } from "../../tenant/tenant";

type Props = {
  contractId?: string;
  action?: string;
  consultantId: string;
  /**
   * Values the ERP sends when a staffer starts this contract from a work
   * request (ERP-741). Folded onto the defaults once, at construction — the
   * form is a normal editable form from that point on.
   */
  erpPrefill?: ErpContractPrefill;
};

export const ContractForm = ({
  consultantId,
  contractId,
  action,
  erpPrefill
}: Props) => {
  const navigator = useNavigation();
  const { tenant } = useTenant();
  const localStore = useLocalStore(
    () =>
      new ContractFormStore(
        applyErpPrefill(initialValues, erpPrefill),
        consultantId,
        () => handleClose(),
        tenant,
        contractId,
        action
      )
  );

  const handleClose = (): void => {
    localStore.form.reset({ ...initialValues, ConsultantId: consultantId });
    localStore._fields = defaultFields;
    setTimeout(() => {
      navigator.close();
    }, 1000);
  };

  const handleOpenTicket = (): void => {
    localStore.isTicket = true;
    localStore.form.submit();
  };

  return useObserver(() => {
    const {
      form,
      loading,
      fields,
      handleTicket,
      ticketData,
      hasErrorsInMainTab,
      isEdit,
      fileName,
      isShowAdditionFields,
      typeOptions,
      showCompanySelect,
      showContractTypeChangeSelect,
      showSendToOneFlow
    } = localStore;

    const renderMain = (
      <Paper
        elevation={0}
        style={{ height: "100%", padding: "12px 24px 36px", position: "relative" }}
      >
        <Typography variant={"h5"}>{fileName ? `${fileName} ` : ""}</Typography>
        <br />
        <SpinnerOverlay show={loading} />

        {showContractTypeChangeSelect ? (
          <p style={{ marginTop: 0 }}>
            <FormControl>
              <FormLabel>{locale.wantToCreateNationalProcurement}</FormLabel>
              <RadioGroup
                name="isContractTypeChange"
                onChange={(e) =>
                  localStore.onChangeContractType(e.target.value as RadioTrueFalse)
                }
              >
                <FormControlLabel value="true" control={<Radio />} label={locale.Yes} />
                <FormControlLabel value="false" control={<Radio />} label={locale.No} />
              </RadioGroup>
            </FormControl>
          </p>
        ) : (
          <>
            {showCompanySelect && (
              <p style={{ marginTop: 0 }}>
                <FormControl
                  disabled={["edit", "duplicate", "forConfirmation"].includes(
                    localStore.action
                  )}
                >
                  <FormLabel>{locale.ChoseCompany}</FormLabel>
                  <RadioGroup
                    name="company"
                    value={localStore.company}
                    onChange={(e) => localStore.setCompany(e.target.value as CompanyName)}
                  >
                    <FormControlLabel value="AB" control={<Radio />} label={locale.AB} />
                    <FormControlLabel value="BC" control={<Radio />} label={locale.BC} />
                  </RadioGroup>
                </FormControl>
              </p>
            )}
            {localStore.hasNationalProcurementSelection && (
              <p>
                <FormControl
                  disabled={
                    localStore.isNationalProcurementDisabled ||
                    localStore.action === "forConfirmation"
                  }
                >
                  <FormLabel>{locale.IsNationalProcurement}</FormLabel>
                  <RadioGroup
                    name="isNationalProcurement"
                    value={localStore.isNationalProcurement}
                    onChange={(e) =>
                      localStore.setNational(e.target.value as RadioTrueFalse)
                    }
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label={locale.Yes}
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label={locale.No}
                    />
                  </RadioGroup>
                </FormControl>
              </p>
            )}

            {localStore.showMainForm && (
              <>
                <DynamicFieldsGroup
                  form={form}
                  fields={!localStore.noFields ? fields : defaultFields}
                  extraProps={{ typeOptions, store: localStore }}
                />
                {localStore.noFields && (
                  <Alert severity="warning">
                    {"Avtal för detta yrke/område är inte implementerat"}
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    );

    const priceFields = fields
      ?.filter(
        (x) =>
          x.name === "SalaryRecords" ||
          x.name === "Bonus" ||
          x.name === "ConsultantGetsPaidPer"
      )
      .map((x) => ({
        ...x,
        show: () => isShowAdditionFields
      }))
      .sortBy((x) => x.order);

    const tabs = [
      {
        label: (
          <TabBadge
            label={isEdit ? locale.Edit : `${locale.New} ${locale.Contract}`}
            hasErrors={hasErrorsInMainTab}
          />
        ),
        value: "main",
        content: renderMain
      },
      {
        label: (
          <TabBadge
            label={locale.SalaryCompensation}
            hasErrors={
              !!form.errors.BasicPrice ||
              !!form.errors.SpecificPeriodPrices ||
              !!form.errors.SalaryRecords
            }
          />
        ),
        value: "price",
        content: (
          <Paper elevation={0} style={{ height: "100%", position: "relative" }}>
            <SpinnerOverlay show={loading} />
            <DynamicFieldsGroup
              skipChangeSchema
              form={form}
              fields={priceFields}
              extraProps={{ typeOptions, store: localStore }}
            />
          </Paper>
        )
      }
    ].filter((x) => {
      return priceFields?.length && isShowAdditionFields ? true : x.value !== "price";
    });

    const isBookingConfirmationContractType =
      form.values.ContractType === ConsultantContractType.Confirmation ||
      form.values.ContractType === ConsultantContractType.ConfirmationForEmployees;

    const hideCreateTicket =
      form.values.ContractType === ConsultantContractType.ConfirmationForEmployees;

    const isSigned = !!localStore.contractData?.SignedOn;

    const extraBtns = (
      <Observer>
        {() => (
          <>
            {isEdit && !isSigned && !isBookingConfirmationContractType && (
              <PrimaryButton
                onClick={() => handleTicket(ticketData)}
                disabled={form.dirty || form.isSubmitting}
              >
                {locale.AddEmail}
              </PrimaryButton>
            )}

            {!isSigned && showSendToOneFlow && (
              <PrimaryButton
                onClick={() => localStore.handleSaveAndSendToOneflow()}
                disabled={!localStore.canSubmit || form.isSubmitting}
                loading={localStore.isPublishingToOneFlow}
              >
                Skapa och skicka till OneFlow
              </PrimaryButton>
            )}

            {!isSigned && !hideCreateTicket && (
              <PrimaryButton
                onClick={handleOpenTicket}
                disabled={!localStore.canSubmit || form.isSubmitting}
                loading={localStore.isTicket && form.isSubmitting}
              >
                {locale.SaveAndCreateEmail}
              </PrimaryButton>
            )}
          </>
        )}
      </Observer>
    );

    const wccBtns = (
      <Observer>
        {() => (
          <>
            {!isSigned && showSendToOneFlow && (
              <PrimaryButton
                onClick={() => localStore.handleSaveAndSendViaWorkflow(true)}
                disabled={!localStore.canSubmit || form.isSubmitting}
                loading={localStore.isPublishingToOneFlow}
              >
                Spara och skicka via OneFlow
              </PrimaryButton>
            )}

            {!isSigned && showSendToOneFlow && (
              <PrimaryButton
                onClick={() => localStore.handleSaveAndSendViaWorkflow(false)}
                disabled={!localStore.canSubmit || form.isSubmitting}
                loading={localStore.isPublishingToOneFlow}
              >
                Spara och ta mig till OneFloW
              </PrimaryButton>
            )}
          </>
        )}
      </Observer>
    );

    return (
      <Form store={form} allowUpdates>
        <TabFormLayout
          blockId="consultant_contract_form"
          canSubmit={localStore.canSubmit && !form.isSubmitting}
          isDirty={form.dirty}
          isSubmitting={
            form.isSubmitting && !localStore.isTicket && !localStore.isPublishingToOneFlow
          }
          onSubmit={form.submit}
          onReset={form.onResetHandler}
          extraButtons={localStore.tenant === "wcc" ? wccBtns : extraBtns}
          onClose={localStore.handleClose}
          tabs={tabs}
        >
          <FormxDebugger store={localStore.form} />
        </TabFormLayout>

        <FormxDebugger store={form} />

        <ContractUploaderDialog open={localStore.isPublishingToOneFlow} />
      </Form>
    );
  });
};
